import { Gender, LiquidityPeriodBalanceType, PolicyOrAccount, Product } from "../../interfaces/DTO/mislaka_xml_models";
import { MimshakService } from "../mimshakService";
import { MimshakPensionService } from "../mimshakPensionService";
import { CalculationResult, ReturnMethodology, SkipReason, TimeToTarget, PensionProjectionEngine } from "./pensionProjectionEngine";


export type CoefficientChoice = "reported" | "estimated";

export interface PensionProjectionOptions {
  /** Birth date — defaults to the product's client.birth_date */
  birthDate?: string | null;
  /** Target age — defaults by the client's gender (67 / 64) */
  targetAge?: number | null;
  /** Annual return (decimal) — defaults to 4% */
  annualReturn?: number;
  returnMethodology?: ReturnMethodology;
  /** Data date — defaults to the latest update_date of the product's policies */
  calculationDate?: string | null;
  /** Monthly contribution — defaults to the reported last deposit; never invented when missing */
  monthlyContribution?: number | null;
  /** Fee on contributions (decimal) — defaults to the investment paths' weighted fee */
  contributionFeeRate?: number | null;
  /** Which coefficient converts annuity capital to a monthly pension */
  coefficientChoice?: CoefficientChoice;
  /** A coefficient supplied by the system / user for estimation (e.g. the calculator's coefficient field) */
  estimatedCoefficient?: number | null;
}

export type ReportedCoefficientSource =
  /** guaranteed_conversion_factor reported on the policy (e.g. old managers insurance) */
  | "guaranteed"
  /** implied by the producer's own projection: projected annuity capital ÷ expected monthly pension */
  | "impliedFromReportedProjection";

export interface ReportedCoefficient {
  value: number;
  source: ReportedCoefficientSource;
}

export interface ContributionInputs {
  /** Monthly contribution used (gross), or null when none is available */
  monthlyContribution: number | null;
  monthlyContributionSource: "override" | "reportedLastDeposit" | null;
  contributionFeeRate: number | null;
  contributionFeeRateSource: "override" | "investmentPaths" | null;
  /** Monthly contribution after the contribution fee */
  netMonthlyContribution: number | null;
  /** Number of monthly contributions until the target date */
  contributionMonths: number | null;
  /** Total gross / net contributions taken into account until the target date */
  totalGrossContributions: number | null;
  totalNetContributions: number | null;
}

export interface ComponentProjection {
  currentBalance: number;
  withoutContributions: CalculationResult<number>;
  /** Only the paying-annuity component receives future contributions — see calculatePensionProductProjection */
  withContributions: CalculationResult<number> | null;
}

export interface AnnuityComponentProjection extends ComponentProjection {
  monthlyPensionWithoutContributions: CalculationResult<number>;
  monthlyPensionWithContributions: CalculationResult<number> | null;
}

/** Producer-reported forecast values — reporting / comparison only, never a calculation input */
export interface ReportedProjectionValues {
  projectedAnnuityCapitalWithoutContributions: number | null;
  projectedAnnuityCapitalWithContributions: number | null;
  expectedMonthlyPension: number | null;
  retirementPensionAmount: number | null;
  projectionReturnRange: string | null;
}

export interface PensionProductProjection {
  inputs: {
    calculationDate: string | null;
    birthDate: string | null;
    targetAge: number | null;
    targetAgeSource: "override" | "defaultByGender" | null;
    targetDate: Date | null;
    timeToTarget: TimeToTarget | null;
    annualReturn: number;
    effectiveAnnualReturn: number;
    returnMethodology: ReturnMethodology;
    accumulationFeeRate: number | null;
  };
  contributions: ContributionInputs;
  /** סכום נוכחי קצבה משלמת */
  payingAnnuity: AnnuityComponentProjection;
  /** סכום נוכחי קצבה לא משלמת */
  nonPayingAnnuity: AnnuityComponentProjection;
  /** סכום נוכחי הוני — stays capital, never converted to a pension */
  capital: ComponentProjection;
  coefficients: {
    reported: ReportedCoefficient | null;
    /** Distinct guaranteed factors when they conflict and none matches the target age */
    reportedCandidates: number[];
    estimated: number | null;
    choice: CoefficientChoice;
    used: { value: number; kind: CoefficientChoice } | null;
  };
  reported: ReportedProjectionValues;
  /** Calculations that were not performed, with the reason */
  skipped: { calculation: string; reason: SkipReason }[];
  /** Assumptions made where data was missing but a calculation could still run */
  warnings: string[];
}

const toDecimalRate = (percent: number | null | undefined): number | null =>
  typeof percent === "number" && Number.isFinite(percent) ? percent / 100 : null;

const latestUpdateDate = (policies: PolicyOrAccount[]): string | null =>
  policies
    .map((policy) => policy.update_date)
    .filter((date): date is string => !!date && date !== "0000-00-00")
    .sort()
    .pop() ?? null;

/**
 * Adapter between a pension Product (mislaka data) and the projection engine.
 * Reads current balances, contributions, fees and coefficients from the product; producer forecast fields are
 * kept only as `reported` values for comparison and never feed the calculation.
 */
export class PensionProductProjectionService {
  /** Default target age by the client's gender — men 67, women 64; unknown → no default */
  static getDefaultTargetAge(gender: Gender | string | null | undefined): number | null {
    return gender === Gender.MALE ? 67 : gender === Gender.FEMALE ? 64 : null;
  }

  /** Sum of each policy's latest reported deposit (last_deposit_summary) — null when none was reported */
  static getReportedMonthlyContribution(policies: PolicyOrAccount[]): number | null {
    const total = policies.reduce((sum, policy) => {
      const latest = [...(policy.last_deposit_summary ?? [])].sort((a, b) =>
        String(b.reference_month).localeCompare(String(a.reference_month))
      )[0];
      return sum + (latest?.total_last_deposit && latest.total_last_deposit > 0 ? latest.total_last_deposit : 0);
    }, 0);
    return total > 0 ? total : null;
  }

  /** Balance-weighted fee rate (decimal) over the policies' investment paths — null when no path reports it */
  static getWeightedPathFeeRate(
    policies: PolicyOrAccount[],
    field: "management_fee_on_deposit" | "management_fee_on_savings"
  ): number | null {
    const paths = policies.flatMap((policy) => policy.investment_paths ?? []).filter((path) => typeof path[field] === "number");
    if (paths.length === 0) return null;
    const totalWeight = paths.reduce((sum, path) => sum + (path.accumulated_amount || 0), 0);
    const percent =
      totalWeight > 0
        ? paths.reduce((sum, path) => sum + path[field] * (path.accumulated_amount || 0), 0) / totalWeight
        : paths.reduce((sum, path) => sum + path[field], 0) / paths.length;
    return toDecimalRate(percent);
  }

  /**
   * The coefficient reported for the product: a guaranteed conversion factor when reported (the row for the target age
   * wins when factors differ), otherwise the coefficient implied by the producer's own projection.
   */
  static getReportedCoefficient(
    product: Product,
    targetAge: number | null
  ): { coefficient: ReportedCoefficient | null; candidates: number[] } {
    const guaranteedRows = product.policies_or_accounts
      .flatMap((policy) => policy.retirement_savings_by_age ?? [])
      .filter((row) => !!row.guaranteed_conversion_factor && row.life_expectancy_guaranteed !== 2);
    const distinct = Array.from(new Set(guaranteedRows.map((row) => row.guaranteed_conversion_factor as number)));

    if (distinct.length === 1) return { coefficient: { value: distinct[0], source: "guaranteed" }, candidates: [] };
    if (distinct.length > 1) {
      const atTargetAge = guaranteedRows.find((row) => row.retirement_age === targetAge)?.guaranteed_conversion_factor;
      return atTargetAge
        ? { coefficient: { value: atTargetAge, source: "guaranteed" }, candidates: [] }
        : { coefficient: null, candidates: distinct };
    }

    const summary = MimshakPensionService.aggregatePensionSummary([product]);
    if (summary.expected_monthly_pension > 0 && summary.total_accumulated_amount_without_premiums > 0) {
      return {
        coefficient: {
          value: summary.total_accumulated_amount_without_premiums / summary.expected_monthly_pension,
          source: "impliedFromReportedProjection",
        },
        candidates: [],
      };
    }
    return { coefficient: null, candidates: [] };
  }

  /** Producer forecast values for the product — kept for comparison only */
  static getReportedProjectionValues(product: Product): ReportedProjectionValues {
    const summary = MimshakPensionService.aggregatePensionSummary([product]);
    const orNull = (value: number) => (value > 0 ? value : null);
    return {
      projectedAnnuityCapitalWithoutContributions: orNull(summary.total_accumulated_amount_without_premiums),
      projectedAnnuityCapitalWithContributions: orNull(summary.total_accumulated_amount_with_premiums),
      expectedMonthlyPension: orNull(summary.expected_monthly_pension),
      retirementPensionAmount: orNull(summary.retirement_pension_amount),
      projectionReturnRange: summary.forecast_yield_percentage_range !== "-" ? summary.forecast_yield_percentage_range : null,
    };
  }

  /**
   * Full projection of one pension product to the target age.
   *
   * - Paying annuity, non-paying annuity and capital are projected separately with the same return; capital never
   *   becomes a pension.
   * - Future contributions are added to the paying-annuity component only (current deposits are annuity money).
   * - A monthly pension is produced only with a coefficient: the reported one, or an explicitly supplied estimate.
   */
  static calculatePensionProductProjection(
    product: Product,
    options: PensionProjectionOptions = {}
  ): PensionProductProjection {
    const policies = product.policies_or_accounts ?? [];
    const skippedList: PensionProductProjection["skipped"] = [];
    const warnings: string[] = [];
    const track = <T>(calculation: string, result: CalculationResult<T>): CalculationResult<T> => {
      if (!PensionProjectionEngine.isCalculated(result)) skippedList.push({ calculation, reason: result.reason });
      return result;
    };

    // --- inputs ---
    const birthDate = options.birthDate ?? product.client?.birth_date ?? null;
    const defaultTargetAge = PensionProductProjectionService.getDefaultTargetAge(product.client?.gender);
    const targetAge = options.targetAge ?? defaultTargetAge;
    const targetAgeSource = options.targetAge != null ? "override" : defaultTargetAge != null ? "defaultByGender" : null;
    const calculationDate = options.calculationDate ?? latestUpdateDate(policies);
    const annualReturn = options.annualReturn ?? PensionProjectionEngine.DEFAULT_ANNUAL_RETURN;
    const returnMethodology = options.returnMethodology ?? "gross";
    const accumulationFeeRate = PensionProductProjectionService.getWeightedPathFeeRate(policies, "management_fee_on_savings");
    const effectiveAnnualReturn = PensionProjectionEngine.resolveEffectiveAnnualReturn(annualReturn, accumulationFeeRate, returnMethodology);

    const targetDate = track("targetDate", PensionProjectionEngine.calculateTargetDate(birthDate, targetAge));
    const time: CalculationResult<TimeToTarget> = PensionProjectionEngine.isCalculated(targetDate)
      ? track("timeToTarget", PensionProjectionEngine.calculateMonthsToTarget(calculationDate, targetDate.value))
      : targetDate;

    // --- contributions ---
    const monthlyContribution = options.monthlyContribution ?? PensionProductProjectionService.getReportedMonthlyContribution(policies);
    const monthlyContributionSource =
      options.monthlyContribution != null ? "override" : monthlyContribution != null ? "reportedLastDeposit" : null;
    const contributionFeeRate = options.contributionFeeRate ?? PensionProductProjectionService.getWeightedPathFeeRate(policies, "management_fee_on_deposit");
    const contributionFeeRateSource =
      options.contributionFeeRate != null ? "override" : contributionFeeRate != null ? "investmentPaths" : null;
    if (monthlyContribution != null && contributionFeeRate == null) {
      warnings.push("לא דווחו דמי ניהול מהפקדה — ההפקדה חושבה ללא ניכוי דמי ניהול");
    }
    const netMonthlyContribution = track(
      "netMonthlyContribution",
      PensionProjectionEngine.calculateNetMonthlyContribution(monthlyContribution, contributionFeeRate)
    );
    const contributionMonths = PensionProjectionEngine.isCalculated(time) ? time.value.months : null;

    // --- balances, each projected separately ---
    const balanceOf = (type: LiquidityPeriodBalanceType) => MimshakService.calculateProductTotalAmount(product, type) || 0;
    const project = (name: string, balance: number) =>
      PensionProjectionEngine.isCalculated(time)
        ? track(`${name}.withoutContributions`, PensionProjectionEngine.calculateFutureValueOfBalance(balance, effectiveAnnualReturn, time.value.years))
        : (time as CalculationResult<number>);

    const payingBalance = balanceOf(LiquidityPeriodBalanceType.PAYING_PENSION);
    const nonPayingBalance = balanceOf(LiquidityPeriodBalanceType.NON_PAYING_PENSION);
    const capitalBalance = balanceOf(LiquidityPeriodBalanceType.CAPITAL);

    const payingWithout = project("payingAnnuity", payingBalance);
    const nonPayingWithout = project("nonPayingAnnuity", nonPayingBalance);
    const capitalWithout = project("capital", capitalBalance);

    const contributionsFV: CalculationResult<number> = PensionProjectionEngine.isCalculated(time)
      ? PensionProjectionEngine.isCalculated(netMonthlyContribution)
        ? track(
            "contributions",
            PensionProjectionEngine.calculateFutureValueOfContributions(netMonthlyContribution.value, effectiveAnnualReturn, time.value.months)
          )
        : netMonthlyContribution
      : (time as CalculationResult<number>);
    const payingWith = track("payingAnnuity.withContributions", PensionProjectionEngine.calculateFutureValueWithContributions(payingWithout, contributionsFV));

    // --- coefficient ---
    const { coefficient: reportedCoefficient, candidates } = PensionProductProjectionService.getReportedCoefficient(product, targetAge);
    const estimatedCoefficient = options.estimatedCoefficient ?? null;
    const coefficientChoice = options.coefficientChoice ?? "reported";
    const usedCoefficientValue = coefficientChoice === "estimated" ? estimatedCoefficient : reportedCoefficient?.value ?? null;
    const usedCoefficient = usedCoefficientValue && usedCoefficientValue > 0 ? { value: usedCoefficientValue, kind: coefficientChoice } : null;
    if (coefficientChoice === "reported" && !reportedCoefficient && candidates.length > 0) {
      skippedList.push({ calculation: "coefficient", reason: "AMBIGUOUS_COEFFICIENT" });
    }
    if (reportedCoefficient?.source === "impliedFromReportedProjection") {
      warnings.push("המקדם המדווח נגזר מתחזית היצרן (צבירה צפויה ÷ קצבה צפויה) ולא דווח במפורש");
    }

    const pension = (name: string, capital: CalculationResult<number>) =>
      track(name, PensionProjectionEngine.calculateEstimatedMonthlyPension(capital, usedCoefficient?.value));

    const totalGrossContributions =
      monthlyContribution != null && contributionMonths != null ? monthlyContribution * contributionMonths : null;
    const totalNetContributions =
      PensionProjectionEngine.isCalculated(netMonthlyContribution) && contributionMonths != null
        ? netMonthlyContribution.value * contributionMonths
        : null;

    return {
      inputs: {
        calculationDate,
        birthDate,
        targetAge,
        targetAgeSource,
        targetDate: PensionProjectionEngine.valueOrNull(targetDate),
        timeToTarget: PensionProjectionEngine.valueOrNull(time),
        annualReturn,
        effectiveAnnualReturn,
        returnMethodology,
        accumulationFeeRate,
      },
      contributions: {
        monthlyContribution,
        monthlyContributionSource,
        contributionFeeRate,
        contributionFeeRateSource,
        netMonthlyContribution: PensionProjectionEngine.valueOrNull(netMonthlyContribution),
        contributionMonths,
        totalGrossContributions,
        totalNetContributions,
      },
      payingAnnuity: {
        currentBalance: payingBalance,
        withoutContributions: payingWithout,
        withContributions: payingWith,
        monthlyPensionWithoutContributions: pension("payingAnnuity.monthlyPensionWithoutContributions", payingWithout),
        monthlyPensionWithContributions: pension("payingAnnuity.monthlyPensionWithContributions", payingWith),
      },
      nonPayingAnnuity: {
        currentBalance: nonPayingBalance,
        withoutContributions: nonPayingWithout,
        withContributions: null,
        monthlyPensionWithoutContributions: pension("nonPayingAnnuity.monthlyPensionWithoutContributions", nonPayingWithout),
        monthlyPensionWithContributions: null,
      },
      capital: {
        currentBalance: capitalBalance,
        withoutContributions: capitalWithout,
        withContributions: null,
      },
      coefficients: {
        reported: reportedCoefficient,
        reportedCandidates: candidates,
        estimated: estimatedCoefficient,
        choice: coefficientChoice,
        used: usedCoefficient,
      },
      reported: PensionProductProjectionService.getReportedProjectionValues(product),
      skipped: skippedList,
      warnings,
    };
  }
}
